import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { featuredArticles, allArticles, forumCategories } from '@/mocks/forumData';
import { articleContents } from '@/mocks/articleContents';
import type { ForumArticle } from '@/mocks/forumData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article: ForumArticle | undefined = useMemo(() => {
    const fromFeatured = featuredArticles.find((a) => a.slug === slug);
    if (fromFeatured) return fromFeatured;
    for (const catArticles of Object.values(allArticles)) {
      const found = catArticles.find((a) => a.slug === slug);
      if (found) return found;
    }
    return undefined;
  }, [slug]);

  const content = article ? (articleContents[article.slug] || '') : '';

  const category = useMemo(
    () => forumCategories.find((c) => c.slug === article?.category),
    [article]
  );

  const relatedArticles: ForumArticle[] = useMemo(() => {
    if (!article) return [];
    const allArticleList = [
      ...featuredArticles,
      ...Object.values(allArticles).flat(),
    ];
    return allArticleList
      .filter(
        (a) => a.slug !== article.slug && a.category === article.category
      )
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-article-line text-2xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-800 mb-2">文章未找到</h2>
          <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接无效</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.thumbnail,
    publisher: {
      '@type': 'Organization',
      name: '草榴论坛入口指南',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
      {
        '@type': 'ListItem',
        position: 2,
        name: category?.name || '版块',
        item: `${window.location.origin}/category/${article.category}`,
      },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <main className="min-h-screen pt-16">
        {/* Article Header */}
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Link
                to={`/category/${article.category}`}
                className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs rounded-md font-medium hover:bg-primary-200 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {category?.name || article.category}
              </Link>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 bg-background-50 border border-background-200/70 text-foreground-500 text-xs rounded-md whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 leading-snug">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-sm"></i></span>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm"></i></span>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-eye-line text-sm"></i></span>
                {article.viewCount.toLocaleString()} 次阅读
              </span>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="w-full py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Main Content */}
              <article className="flex-1">
                <div
                  className="prose-content text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: content }}
                />

                {/* Article Footer */}
                <div className="mt-10 pt-6 border-t border-background-200/70">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <Link
                      to={`/category/${article.category}`}
                      className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200 cursor-pointer"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line text-sm"></i></span>
                      返回{category?.name || '版块'}
                    </Link>
                    <div className="flex items-center gap-3 text-sm text-foreground-500">
                      <span>文章有帮助吗？</span>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 hover:text-accent-600 transition-colors duration-200 cursor-pointer">
                        <i className="ri-thumb-up-line"></i>
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-secondary-100 hover:text-secondary-600 transition-colors duration-200 cursor-pointer">
                        <i className="ri-thumb-down-line"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="w-full lg:w-64 flex-shrink-0">
                <div className="lg:sticky lg:top-28 space-y-5">
                  <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">文章信息</h3>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">作者</dt>
                        <dd className="text-foreground-800 font-medium">{article.author}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">发布</dt>
                        <dd className="text-foreground-800">{article.publishDate}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">阅读</dt>
                        <dd className="text-foreground-800">{article.viewCount.toLocaleString()}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-foreground-500">评论</dt>
                        <dd className="text-foreground-800">{article.commentCount}</dd>
                      </div>
                    </dl>
                  </div>

                  {relatedArticles.length > 0 && (
                    <div className="bg-background-100 border border-background-200/70 rounded-lg p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-3">相关文章</h3>
                      <div className="space-y-3">
                        {relatedArticles.map((ra) => (
                          <Link
                            key={ra.id}
                            to={`/article/${ra.slug}`}
                            className="block group cursor-pointer"
                          >
                            <p className="text-xs font-medium text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                              {ra.title}
                            </p>
                            <p className="text-xs text-foreground-500 mt-1">{ra.publishDate}</p>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}